import * as api from '@apis/admin-affiliates';
import { AffiliateListItem, AffiliateSearchQuery, PaymentStatementWithAttribution } from '@models/affiliate';
import { PagenatedResponse } from '@models/common';
import { useAuthentication } from '@hooks/authentication';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Badge, Button, Input, Table, Switch, Modal, DatePicker } from 'antd';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { DatePickerProps } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import dayjs from 'dayjs';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { PaymentStatement } from '@models/paymentStatement';
import { localTimeToUtcISOString, utcTimeToLocalISOString } from '@utils/date';

const REFUND_STATUSES: { [key: string]: any } = {
  all: <div style={{ color: 'red' }}>전체 환불</div>,
  partial: <div style={{ color: '#d944db' }}>부분 환불</div>,
  requested: <div style={{ color: '#db9737' }}>환불 요청</div>,
  accepted: <div style={{ color: '#2782b3' }}>환불 예정</div>,
  denied: <div style={{ color: '#6e6e6e' }}>환불 반려</div>,
  reserved: <div style={{ color: 'green' }}>환불 예약</div>,
  request_cancelled: <div style={{ color: 'tomato' }}>환불 철회</div>,
  submission: <div style={{ color: 'pink' }}>환불 접수</div>,
  none: <div>결제 완료</div>,
};

const AffiliatesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'product' | 'campaign'>('product');
  const [searchQuery, setSearchQuery] = useState<AffiliateSearchQuery>({
    page: 0,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    targetType: 'product',
    searchText: undefined,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined);
  const [userIdSearch, setUserIdSearch] = useState<string>('');
  const [productIdSearch, setProductIdSearch] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPaymentStatements, setSelectedPaymentStatements] = useState<PaymentStatement[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  useAuthentication();
  const dialog = useOkCancelDialog();

  const affiliatesQuery = useQuery({
    queryKey: ['admin-affiliates', JSON.stringify(searchQuery)],
    queryFn: () => api.searchAffiliates(searchQuery),
  });

  const updateStatusMutation = useMutation({
    mutationFn: api.updateAffiliateStatus,
    onSuccess: (data) => {
      affiliatesQuery.refetch();
      toast.success(`Affiliate가 ${data.isActive ? '활성화' : '비활성화'}되었습니다.`);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || '상태 업데이트에 실패했습니다.');
    },
  });

  useEffect(() => {
    setSearchQuery((prev) => {
      const newQuery: AffiliateSearchQuery = {
        page: 0,
        limit: pageSize,
        sortBy: prev.sortBy,
        sortOrder: prev.sortOrder,
        targetType: activeTab,
      };
      if (prev.startDate) {
        newQuery.startDate = prev.startDate;
      }
      if (prev.endDate) {
        newQuery.endDate = prev.endDate;
      }
      if (isActiveFilter !== undefined) {
        newQuery.isActive = isActiveFilter;
      }
      if (userIdSearch.trim()) {
        newQuery.userId = userIdSearch.trim();
      }
      if (productIdSearch.trim()) {
        newQuery.productId = productIdSearch.trim();
      }
      return newQuery;
    });
    setCurrentPage(1);
  }, [activeTab, isActiveFilter, userIdSearch, productIdSearch, pageSize]);

  // 페이지 변경 시 searchQuery 업데이트
  useEffect(() => {
    setSearchQuery((prev) => ({
      ...prev,
      page: currentPage - 1,
    }));
  }, [currentPage]);

  const handleDownload = async () => {
    const fileName = `igogo-affiliates-${activeTab}-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
    setIsDownloading(true);
    try {
      const blob = await api.downloadAffiliatesExcel(searchQuery);
      const url = window.URL.createObjectURL(blob.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || '검색결과 다운로드에 실패했습니다.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleToggleStatus = (affiliateId: string, currentStatus: boolean) => {
    dialog.open({
      content: `Affiliate를 ${currentStatus ? '비활성화' : '활성화'}하시겠습니까?`,
      onConfirm: () => {
        updateStatusMutation.mutate({
          affiliateId,
          isActive: !currentStatus,
        });
      },
    });
  };

  const productOnlyColumns: ColumnsType<AffiliateListItem> = [
    {
      title: '결제/환불 금액',
      width: 120,
      align: 'right',
      render: (record: AffiliateListItem) => {
        return (
          <div>
            {(record.totalPaymentAmount || 0).toLocaleString()}
            <div style={{ color: 'red' }}>{`환불: (${(record.totalRefundAmount || 0).toLocaleString()})`}</div>
          </div>
        );
      },
    },
    {
      title: '적립 예정 포인트',
      width: 150,
      align: 'right',
      render: (record: AffiliateListItem) => {
        const accumulatedPointAmount = record.accumulatedPointAmount || 0;
        const netAmount = (record.totalPointAmount || 0) - (record.refundPointAmount || 0) - accumulatedPointAmount;
        return `${(netAmount || 0).toLocaleString()}P`;
      },
    },
  ];

  const columns: ColumnsType<AffiliateListItem> = [
    {
      title: 'Affiliate ID',
      dataIndex: 'affiliateId',
      key: 'affiliateId',
      width: 100,
      render: (affiliateId: string) => (
        <Button
          style={{ alignContent: 'center' }}
          onClick={() => {
            navigator.clipboard.writeText(affiliateId);
            toast.dark('Affiliate ID를 클립보드에 복사했습니다', {
              position: 'top-center',
              autoClose: 3000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              pauseOnFocusLoss: false,
              draggable: true,
              progress: undefined,
              bodyStyle: {
                color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                textAlign: 'center',
                fontFamily: 'Pretendard',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '20px',
              },
              theme: 'dark',
            });
          }}>
          ID 복사
        </Button>
      ),
    },
    {
      title: activeTab === 'campaign' ? '캠페인명' : '상품명',
      key: 'productName',
      width: 200,
      render: (record: AffiliateListItem) => {
        const targetName = record.targetName || record.productName || '-';
        const targetId = record.targetId || record.productId;
        return (
          <div>
            <div>{targetName}</div>
            <Button
              disabled={!targetId}
              style={{ alignContent: 'center' }}
              onClick={() => {
                navigator.clipboard.writeText(targetId || '');
                toast.dark(`${activeTab === 'campaign' ? '캠페인' : '상품'} ID를 클립보드에 복사했습니다`, {
                  position: 'top-center',
                  autoClose: 3000,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: true,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  bodyStyle: {
                    color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                    textAlign: 'center',
                    fontFamily: 'Pretendard',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '20px',
                  },
                  theme: 'dark',
                });
              }}>
              ID 복사
            </Button>
          </div>
        );
      },
    },
    {
      title: '공유자',
      align: 'center',
      key: 'userId',
      width: 120,
      render: (record: AffiliateListItem) => {
        const user = record?.user || {};
        return (
          <div>
            <div>{user?.profile?.name || '-'}</div>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(user?.profile?.phone || '');
                toast.info('전화번호를 클립보드에 복사했습니다.', {
                  position: 'top-center',
                  autoClose: 5000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  theme: 'light',
                });
              }}>
              {user?.profile?.phone || '-'}
            </Button>
            <Button
              style={{ alignContent: 'center' }}
              onClick={() => {
                navigator.clipboard.writeText(user?.id || '');
                toast.dark('사용자 ID를 클립보드에 복사했습니다', {
                  position: 'top-center',
                  autoClose: 3000,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: true,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  bodyStyle: {
                    color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                    textAlign: 'center',
                    fontFamily: 'Pretendard',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '20px',
                  },
                  theme: 'dark',
                });
              }}>
              ID 복사
            </Button>
          </div>
        );
      },
    },
    {
      title: '링크 URL',
      dataIndex: 'linkUrl',
      key: 'linkUrl',
      width: 300,
      render: (linkUrl: string) => (
        <a href={linkUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
          {linkUrl}
        </a>
      ),
    },
    {
      title: '활성화 상태',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (isActive: boolean, record: AffiliateListItem) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Badge status={isActive ? 'success' : 'default'} text={isActive ? '활성화' : '비활성화'} />
          <Switch
            checked={isActive}
            onChange={() => handleToggleStatus(record.affiliateId, isActive)}
            loading={updateStatusMutation.isPending}
          />
        </div>
      ),
    },
    {
      title: '방문 수',
      dataIndex: 'visitCount',
      key: 'visitCount',
      width: 100,
      align: 'right',
      render: (visitCount: number) => visitCount.toLocaleString(),
    },
    {
      title: activeTab === 'campaign' ? '적립수' : '결제 완료 수',
      width: 120,
      align: 'right',
      render: (record: AffiliateListItem) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div>
              {(record.paymentCount || 0).toLocaleString()}
              {(record.refundCount || 0) > 0 && (
                <div style={{ color: 'red' }}>{`환불: (${record.refundCount.toLocaleString()})`}</div>
              )}
            </div>
            {activeTab === 'product' && (
              <Button
                onClick={() => {
                  setSelectedPaymentStatements(record.paymentStatements || []);
                  setIsModalOpen(true);
                }}
                style={{ padding: 0, marginTop: 4 }}>
                상세보기
              </Button>
            )}
          </div>
        );
      },
    },
    ...(activeTab === 'product' ? productOnlyColumns : []),
    {
      title: '적립 완료 포인트',
      width: 150,
      align: 'right',
      render: (record: AffiliateListItem) => {
        return `${(record.accumulatedPointAmount || 0).toLocaleString()}P`;
      },
    },
    {
      title: '생성일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (createdAt: Date) => moment(createdAt).format('YYYY.MM.DD HH:mm'),
    },
    {
      title: '비활성화일',
      dataIndex: 'deactivatedAt',
      key: 'deactivatedAt',
      width: 160,
      render: (deactivatedAt?: Date) => (deactivatedAt ? moment(deactivatedAt).format('YYYY.MM.DD HH:mm') : '-'),
    },
  ];

  const data = affiliatesQuery.data as PagenatedResponse<AffiliateListItem> | undefined;

  const onChangeStartDate: DatePickerProps['onChange'] = (date) => {
    setSearchQuery((prev) => {
      const newQuery = { ...prev, page: 0 };
      if (date) {
        newQuery.startDate = localTimeToUtcISOString(new Date(date.toISOString()), {
          timeToZero: true,
        }) as string;
      } else {
        delete newQuery.startDate;
      }
      return newQuery;
    });
    setCurrentPage(1);
  };

  const onChangeEndDate: DatePickerProps['onChange'] = (date) => {
    setSearchQuery((prev) => {
      const newQuery = { ...prev, page: 0 };
      if (date) {
        newQuery.endDate = localTimeToUtcISOString(new Date(date.toISOString()), {
          timeToZero: true,
        }) as string;
      } else {
        delete newQuery.endDate;
      }
      return newQuery;
    });
    setCurrentPage(1);
  };

  const handleSortChange = (sortBy: AffiliateSearchQuery['sortBy']) => {
    setSearchQuery((prev) => ({
      ...prev,
      sortBy,
      page: 0,
    }));
    setCurrentPage(1);
  };

  const handleSortOrderChange = (sortOrder: 'asc' | 'desc') => {
    setSearchQuery((prev) => ({
      ...prev,
      sortOrder,
      page: 0,
    }));
    setCurrentPage(1);
  };

  const paymentStatementColumns: ColumnsType<PaymentStatementWithAttribution> = [
    {
      title: '구매자명',
      key: 'buyerName',
      width: 120,
      render: (record: PaymentStatementWithAttribution) => {
        return (
          <div>
            <div>{record.buyer?.name || '-'}</div>
            <div>{record.buyer?.phone || '-'}</div>
          </div>
        );
      },
    },
    {
      title: '결제 금액',
      key: 'paymentAmount',
      width: 120,
      align: 'right',
      render: (record: PaymentStatementWithAttribution) => {
        return (
          <div>
            <div>
              {(record.payment?.amount || 0).toLocaleString()}원
              {record.payment?.point ? `(${record.payment?.point?.toLocaleString()}P)` : ''}
            </div>
            <Button
              style={{ alignContent: 'center' }}
              onClick={() => {
                navigator.clipboard.writeText(record.uuid || '');
                toast.dark('주문번호를 클립보드에 복사했습니다', {
                  position: 'top-center',
                  autoClose: 3000,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: true,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  bodyStyle: {
                    color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                    textAlign: 'center',
                    fontFamily: 'Pretendard',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '20px',
                  },
                  theme: 'dark',
                });
              }}>
              주문번호 복사
            </Button>
          </div>
        );
      },
    },
    {
      title: '결제상태',
      dataIndex: ['statuses', 'refund'],
      key: 'refundStatus',
      width: 120,
      align: 'center',
      render: (status: string) => <div style={{ marginBottom: '3px' }}>{REFUND_STATUSES[status] || '-'}</div>,
    },
    {
      title: '결제일시',
      dataIndex: ['payment', 'paidAt'],
      key: 'paidAt',
      width: 160,
      render: (paidAt: Date) => (paidAt ? moment(paidAt).format('YYYY.MM.DD HH:mm') : '-'),
    },
    {
      title: '적립예정일시',
      dataIndex: ['attribution', 'pointAccumulationScheduledAt'],
      key: 'pointAccumulationScheduledAt',
      width: 160,
      render: (pointAccumulationScheduledAt: Date) =>
        pointAccumulationScheduledAt ? moment(pointAccumulationScheduledAt).format('YYYY.MM.DD HH:mm') : '-',
    },
    {
      title: '적립 포인트',
      dataIndex: ['attribution', 'pointAmount'],
      key: 'pointAmount',
      width: 120,
      align: 'right',
      render: (pointAmount: number) => <div>{pointAmount.toLocaleString()}P</div>,
    },
    {
      title: '적립여부',
      dataIndex: ['attribution', 'pointAccumulated'],
      key: 'pointAccumulated',
      width: 120,
      align: 'center',
      render: (pointAccumulated: boolean) => (
        <div style={{ marginBottom: '3px' }}>{pointAccumulated ? '적립' : '미적립'}</div>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <h2>Affiliate 링크 관리</h2>

      {/* 기간 및 정렬 영역 */}
      <div
        style={{
          marginBottom: '16px',
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}>
        <DatePicker
          onChange={onChangeStartDate}
          value={searchQuery.startDate ? dayjs(utcTimeToLocalISOString(searchQuery.startDate as any)) : undefined}
          placeholder="시작일"
        />
        <span>~</span>
        <DatePicker
          onChange={onChangeEndDate}
          value={searchQuery.endDate ? dayjs(utcTimeToLocalISOString(searchQuery.endDate as any)) : undefined}
          placeholder="종료일"
        />
        <FilterSelect
          inactiveValue="createdAt"
          style={{ width: 150 }}
          value={searchQuery.sortBy}
          onChange={handleSortChange}
          options={[
            { label: '생성일', value: 'createdAt' },
            { label: '방문 수', value: 'visitCount' },
            { label: '결제 완료 수', value: 'paymentCount' },
            { label: '결제 금액', value: 'totalPaymentAmount' },
          ]}
        />
        <FilterSelect
          inactiveValue="desc"
          style={{ width: 120 }}
          value={searchQuery.sortOrder}
          onChange={handleSortOrderChange}
          options={[
            { label: '내림차순', value: 'desc' },
            { label: '오름차순', value: 'asc' },
          ]}
        />
      </div>

      {/* 검색 및 필터 영역 */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <FilterSelect
          inactiveValue="all"
          placeholder="활성화 상태"
          style={{ width: 150 }}
          value={isActiveFilter === undefined ? 'all' : isActiveFilter}
          onChange={(value) => setIsActiveFilter(value === 'all' ? undefined : (value as boolean))}
          options={[
            { label: '전체', value: 'all' },
            { label: '활성화', value: true },
            { label: '비활성화', value: false },
          ]}
        />
        <Input
          className={filterActiveClassName(!!searchQuery.searchText)}
          placeholder={`공유자/${activeTab === 'campaign' ? '캠페인' : '상품'} ID, 이름, 연락처`}
          value={searchQuery.searchText}
          onChange={(e) => setSearchQuery((prev) => ({ ...prev, searchText: e.target.value, page: 0 }))}
          style={{ width: 200 }}
        />
        <Button
          onClick={() => {
            setUserIdSearch('');
            setProductIdSearch('');
            setIsActiveFilter(undefined);
            setSearchQuery({
              page: 0,
              limit: pageSize,
              sortBy: 'createdAt',
              sortOrder: 'desc',
              targetType: activeTab,
            });
            setCurrentPage(1);
          }}>
          검색초기화
        </Button>
        <div style={{ fontWeight: 'bold' }}>검색결과: {data?.total || 0}</div>
        <Button loading={isDownloading} onClick={handleDownload}>
          검색결과 다운로드
        </Button>
      </div>

      <div style={{ marginBottom: '12px', display: 'flex', gap: '8px' }}>
        <Button type={activeTab === 'product' ? 'primary' : 'default'} onClick={() => setActiveTab('product')}>
          상품
        </Button>
        <Button type={activeTab === 'campaign' ? 'primary' : 'default'} onClick={() => setActiveTab('campaign')}>
          캠페인
        </Button>
      </div>

      {/* 테이블 */}
      <Table
        columns={columns}
        dataSource={data?.contents || []}
        rowKey="affiliateId"
        loading={affiliatesQuery.isPending}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: data?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: (page, newPageSize) => {
            setCurrentPage(page);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
            } else {
              // 페이지만 변경된 경우
              setSearchQuery((prev) => ({
                ...prev,
                page: page - 1,
              }));
            }
          },
        }}
      />

      {/* 결제 내역 상세보기 모달 */}
      <Modal
        title="결제 내역 상세"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsModalOpen(false)}>
            닫기
          </Button>,
        ]}
        width={1000}>
        <Table
          columns={paymentStatementColumns}
          dataSource={selectedPaymentStatements}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
        />
      </Modal>
    </div>
  );
};

export default AffiliatesPage;
